import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  MaxCountsResultData,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';

import { Messages } from '../../../gen-src/Localize';
import {
  TableDownstreamVirtualConnectionTablesFilteredByColumnQuery,
  TableDownstreamVirtualConnectionTablesFilteredByColumnQueryVariables,
} from '../../../gen-src/queries/TableDownstreamVirtualConnectionTablesFilteredByColumnQuery';
import {
  TableDownstreamVirtualConnectionTablesQuery,
  TableDownstreamVirtualConnectionTablesQueryVariables,
} from '../../../gen-src/queries/TableDownstreamVirtualConnectionTablesQuery';
import {
  VirtualConnectionTablesDisplayGridRowDataFragment,
} from '../../../gen-src/queries/VirtualConnectionTablesDisplayGridRowDataFragment';
import { VirtualConnectionTableSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  TableDownstreamVirtualConnectionTablesFilteredByColumnQueryGql,
} from '../../queries/TableDownstreamVirtualConnectionTablesFilteredByColumnQueryGql';
import {
  TableDownstreamVirtualConnectionTablesQueryGql,
} from '../../queries/TableDownstreamVirtualConnectionTablesQueryGql';
import { VirtualConnectionTablesDisplayGrid } from '../sharedDisplayGrid/VirtualConnectionTablesDisplayGrid';
import { VirtualConnectionTablesValidSortFields } from '../sorting/sortProviders/VirtualConnectionTablesGridSortProvider';
import {
  getRowDataWithMaxCountsForVirtualConnectionTable,
} from '../table/functions/VirtualConnectionTableRowDataFunctions';
import { VirtualConnectionTablesDisplayGridRowData } from "../table/types/VirtualConnectionTablesDisplayGridRowData";


type QueryVariables = TableDownstreamVirtualConnectionTablesQueryVariables;
type QueryResults = TableDownstreamVirtualConnectionTablesQuery;
type QueryResultListNode = VirtualConnectionTablesDisplayGridRowDataFragment;
type GridRowData = VirtualConnectionTablesDisplayGridRowData;

type QueryVariablesWithFilter = TableDownstreamVirtualConnectionTablesFilteredByColumnQueryVariables;
type QueryResultsWithFilter = TableDownstreamVirtualConnectionTablesFilteredByColumnQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  return table.downstreamVirtualConnectionTablesConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNode> | null {
  const table = resultData && resultData.tables && resultData.tables[0];
  const column = table && table.columns && table.columns[0];
  return column.downstreamVirtualConnectionTablesConnection;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  const table = resultData && resultData.tables && resultData.tables[0];
  return table;
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData {
  const table = resultData && resultData.tables && resultData.tables[0];
  const column = table && table.columns && table.columns[0];
  return column;
}

function getRowDataForTable(
  table: VirtualConnectionTablesDisplayGridRowDataFragment,
  resultData: QueryResults,
): VirtualConnectionTablesDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnectionTable(table, selectMaxCountsResult(resultData));
}

function getRowDataForTableForFilteredQuery(
  table: VirtualConnectionTablesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): VirtualConnectionTablesDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnectionTable(table, selectMaxCountsResultForFilteredQuery(resultData));
}

export type TableDownstreamVirtualConnectionTablesQueryGridProps =
  GridSortProps<VirtualConnectionTablesValidSortFields> & {
    readonly tableId: string;
    readonly filteredColumnId: string | null;
    readonly rowCount: number | null;
  };

export function TableDownstreamVirtualConnectionTablesQueryGrid(props: TableDownstreamVirtualConnectionTablesQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyDownstreamVirtualConnectionTablesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredTablesLineageGridMessage();

  return props.filteredColumnId ? (
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={TableDownstreamVirtualConnectionTablesFilteredByColumnQueryGql}
      partialQueryVariables={{
        tableId: props.tableId,
        columnId: props.filteredColumnId,
        orderBy: props.orderBy as VirtualConnectionTableSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForTableForFilteredQuery}
    >
      {({ rowStore }) =>
        <VirtualConnectionTablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={TableDownstreamVirtualConnectionTablesQueryGql}
      partialQueryVariables={{
        tableId: props.tableId,
        orderBy: props.orderBy as VirtualConnectionTableSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForTable}
    >
      {({ rowStore }) =>
        <VirtualConnectionTablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  );
}
